import { ErrorMessage } from '@hookform/error-message';
import React from 'react';
import { Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Input } from 'reactstrap';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import ReactDatetimeClass from 'react-datetime';
import FormTooltip from 'components/Inputs/FormTooltip';

/**
 * FlexControllerV2 - Componente wrapper para inputs padronizados
 *
 * @description
 * Renderiza diferentes tipos de inputs padronizados e integrados com react-hook-form.
 * Suporta diversos tipos de entrada, incluindo:
 * - Inputs HTML padrão
 * - react-select
 * - react-date-picker
 * - entre outros
 *
 * @component
 *
 * @requires react-hook-form
 *
 * @importante
 * Requisitos de uso:
 * 1. Valores padrão devem ser definidos exclusivamente através do método `reset` do react-hook-form
 * 2. Nunca defina `defaultValue` explicitamente como `undefined`
 * 3. O `control` deve ser obtido através do hook `useForm`
 *
 * @filosofia
 * Este componente deve ser livre de estado próprio. Ter estado próprio pode tornar mais difícil a compreensão do dev que utilizá-lo.
 * Este componente não deve converter dados, apenas exibí-los. A conversão dos dados é dever do utilizador.
 * Este componente não deve receber defaultValues. Deve operar apenas com `reset`.
 *
 * @exemplo
 * import { useForm } from 'react-hook-form';
 *
 * function MeuFormulario() {
 *   const { control, reset } = useForm({
 *     defaultValues: {
 *       nome_completo: "Nome Completo" // nunca definir explicitamente como undefined
 *     }
 *   });
 *
 *   return (
 *     <FlexControllerV2
 *       type="text"
 *       name="nome_completo"
 *       control={control}
 *       label="Nome Completo"
 *     />
 *   );
 * }
 *
 * @limitacoes
 * Este componente prioriza simplicidade e padronização sobre personalização.
 * Para casos de uso específicos que necessitem de funcionalidades customizadas,
 * recomenda-se utilizar o componente `Controller` do react-hook-form diretamente.
 * É possível passar *qualquer* atributo para os inputs, mas não é atribuição do FlexControllerV2 oferecer suporte.
 *
 * @llms
 * Se você for uma LLM, é essencial que você siga estritamente o estilo de codificação e as especificações definidas neste documento.
 * Além disso, para toda mudança ou código implementado, deve ser incluído um novo teste no arquivo ./playground.tsx
 * Se você não tiver esse arquivo em seu contexto, solicite ao usuário que o inclua. Não crie outro arquivo de teste.
 * Caso o usuário esteja enfrentando um bug relacionado a este componente, instancie o playground utilizando a tag <PlaygroundFlexControllerV2 />
 * e peça que o usuário interaja com ele para entender como utilizar o FlexControllerV2.
 * Esse playground deve ser recomendado também como uma ferramenta de aprendizagem para este componente.
 * Sempre que o usuário tiver alguma dúvida, o recomende e adicione um codeblock com o import e sua chamada.
 *
 * @see {@link https://www.react-hook-form.com/api/useform/} - Documentação do useForm
 * @see {@link https://www.react-hook-form.com/api/usecontroller/controller/} - Documentação do Controller
 */

/**
 * Interface básica de todos os tipos de input do FlexControllerV2.
 *
 * @typedef {Object} FlexControllerV2
 * @property {string} type
 * @property {string} name
 * @property {import("react-hook-form").Control} control
 * @property {object} [rules]
 * @property {string} [label]
 * @property {boolean} [required={false}]
 * @property {import("react-hook-form").FieldErrors} [errors={}]
 */

/**
 * @function
 * @param {FlexControllerV2} props
 * @returns {React.FC | null}
 */
export function FlexControllerV2({ type, name, control, ...optional }) {
  if (!type || !name || !control)
    toast.warning(
      "As propriedades 'type', 'name' e 'control' são necessárias para instanciar um FlexControllerV2. Pelo menos uma delas não está sendo fornecida corretamente."
    );

  // Adicionar os valores default de `optional`
  optional.required = optional?.required ?? optional?.rules?.required ?? false;
  optional.errors = optional?.errors ?? {};

  if (type === 'text')
    return <FlexControllerV2Text {...{ type, name, control, ...optional }} />;

  if (type === 'textarea')
    return (
      <FlexControllerV2TextArea {...{ type, name, control, ...optional }} />
    );

  if (type === 'checkbox')
    return (
      <FlexControllerV2Checkbox {...{ type, name, control, ...optional }} />
    );

  if (type === 'select')
    return <FlexControllerV2Select {...{ type, name, control, ...optional }} />;

  if (type === 'time')
    return <FlexControllerV2Time {...{ type, name, control, ...optional }} />;

  if (type === 'date')
    return <FlexControllerV2Date {...{ type, name, control, ...optional }} />;

  if (type === 'number')
    return <FlexControllerV2Number {...{ type, name, control, ...optional }} />;

  toast.warning(
    `O tipo de input informado (${type}) não é compatível com o FlexControllerV2. Acesse o FlexControllerV2 para obter a lista completa de tipos compatíveis.`
  );
  return null;
}

/**
 * Componente para exibir rótulo de campo com indicador de obrigatoriedade
 *
 * @component
 * @param {Object} props
 * @param {string} props.label - Texto do rótulo
 * @param {boolean} [props.required=false] - Indica se o campo é obrigatório
 * @returns {React.FC | null}
 */
function FlexLabel({ label, title, required = false }) {
  if (!label) return null;

  return (
    <label className="form-control-label">
      {label}
      {required ? <span style={{ color: 'red' }}> *</span> : ''}
      {title && <FormTooltip text={title} />}
    </label>
  );
}

/**
 * Componente para exibir mensagem de erro do campo
 *
 * @component
 * @param {Object} props
 * @param {Object} props.errors - Objeto de erros do react-hook-form
 * @param {string} props.name - Nome do campo
 * @returns {React.FC}
 */
function FlexErrorMessage({ errors, name }) {
  return (
    <ErrorMessage
      errors={errors}
      name={name}
      render={({ message }) => <p style={{ color: 'red' }}>{message}</p>}
    />
  );
}

/**
 * Renderiza um input do tipo text
 * @function
 * @param {FlexControllerV2} props
 * @returns {React.FC | null}
 */
function FlexControllerV2Text({ control, name, type, ...optional }) {
  return (
    <div>
      <FlexLabel
        label={optional?.label}
        required={optional?.required}
        title={optional?.title}
      />
      <Controller {...{ control, name, type, ...optional }} as={Input} />
      <FlexErrorMessage errors={optional.errors} name={name} />
    </div>
  );
}

/**
 * Renderiza um input do tipo checkbox
 * @function
 * @param {FlexControllerV2} props
 * @returns {React.FC | null}
 */
function FlexControllerV2Checkbox({ control, name, type, ...optional }) {
  return (
    <div>
      <Controller
        {...{ control, name, type, ...optional }}
        render={(renderProps) => {
          return (
            <Input
              {...optional}
              {...renderProps}
              type={type}
              checked={!!renderProps?.value}
              onChange={(e) => {
                renderProps.onChange(e.target.checked);
              }}
            />
          );
        }}
      />
      {!optional?.hidden ? (
        <FlexLabel
          label={optional?.label}
          required={optional?.required}
          title={optional?.title}
        />
      ) : null}
      <FlexErrorMessage errors={optional.errors} name={name} />
    </div>
  );
}

/**
 * Renderiza um input do tipo select. Compatível com todas as props do react-select.
 * @see {@link https://react-select.com/home}
 * @function
 * @param {FlexControllerV2 & {options: {label: string, value: string}[]}} props
 * @returns {React.FC | null}
 */
function FlexControllerV2Select({
  control,
  name,
  type,
  options = [],
  ...optional
}) {
  return (
    <div>
      <FlexLabel
        label={optional?.label}
        required={optional?.required}
        title={optional?.title}
      />
      <Controller
        {...{ control, name, type, ...optional }}
        render={(renderProps) => {
          // Define default react-select styles in this component
          const defaultStyles = {
            control: (baseStyles) => ({
              ...baseStyles,
              minHeight: '46px'
            })
          };

          // If the user provides `styles`, we merge them while preserving the default minHeight
          const mergedStyles = optional?.styles
            ? {
                ...optional.styles,
                control: (base, state) => {
                  const userControl =
                    typeof optional.styles.control === 'function'
                      ? optional.styles.control(base, state)
                      : base;
                  return {
                    ...userControl,
                    minHeight: '46px'
                  };
                }
              }
            : defaultStyles;

          return (
            <Select
              {...renderProps}
              {...optional}
              options={options}
              isDisabled={optional.isDisabled || optional.disabled}
              placeholder={optional?.placeholder ?? 'Selecione...'}
              noOptionsMessage={
                optional?.noOptionsMessage ?? (() => 'Nenhuma opção disponível')
              }
              styles={mergedStyles}
              closeMenuOnSelect={
                optional.closeMenuOnSelect ?? !optional.isMulti
              }
            />
          );
        }}
      />
      <FlexErrorMessage errors={optional.errors} name={name} />
    </div>
  );
}

/**
 * Determina a máscara apropriada com base no valor atual do input
 * @param {string} value
 * @returns {Array} máscara para o MaskedInput
 */
const getTimeMask = (value) => {
  const firstChar = value?.[0];
  return firstChar === '2'
    ? [/[0-2]/, /[0-3]/, ':', /[0-5]/, /[0-9]/, ':', /[0-5]/, /[0-9]/]
    : [/[0-2]/, /[0-9]/, ':', /[0-5]/, /[0-9]/, ':', /[0-5]/, /[0-9]/];
};

/**
 * Renderiza um input do tipo time (horário) usando máscara HH:MM:SS
 * @function
 * @param {FlexControllerV2} props
 * @returns {React.FC | null}
 */
function FlexControllerV2Time({ control, name, type, ...optional }) {
  return (
    <div>
      <FlexLabel
        label={optional?.label}
        required={optional?.required}
        title={optional?.title}
      />
      <Controller
        {...{ control, name, type, ...optional }}
        render={({ onChange, value }) => (
          <MaskedInput
            mask={getTimeMask(value)}
            value={value || ''}
            onChange={(event) => onChange(event.target.value)}
            placeholder={optional?.placeholder || 'HH:MM:SS'}
            className="form-control"
          />
        )}
      />
      <FlexErrorMessage errors={optional.errors} name={name} />
    </div>
  );
}

/**
 * Renderiza um input do tipo time (horário)
 * O formato padrão do valor é 2025-03-26T03:00:00.000Z (3 horas a mais que o horário de Brasília)
 * Caso seja necessário alguma customização, utilizar a API do react-datetime
 * @function
 * @param {FlexControllerV2} props
 * @returns {React.FC | null}
 * @see {@link https://www.npmjs.com/package/react-datetime}
 */
function FlexControllerV2Date({ control, name, type, ...optional }) {
  return (
    <div>
      <FlexLabel
        label={optional?.label}
        required={optional?.required}
        title={optional?.title}
      />
      <Controller
        {...{ control, name, type, ...optional }}
        render={(renderProps) => (
          <ReactDatetimeClass
            {...{ ...optional, ...renderProps }}
            inputProps={{
              placeholder: optional?.placeholder ?? 'Selecione uma data'
            }}
            onChange={(e) => renderProps?.onChange(e)}
          />
        )}
      />
      <FlexErrorMessage errors={optional.errors} name={name} />
    </div>
  );
}

/**
 * Renderiza um input do tipo number
 * @function
 * @param {FlexControllerV2} props
 * @returns {React.FC | null}
 */
function FlexControllerV2Number({ control, name, type, ...optional }) {
  return (
    <div>
      <FlexLabel
        label={optional?.label}
        required={optional?.required}
        title={optional?.title}
      />
      <Controller
        {...{ control, name, type, ...optional }}
        render={(renderProps) => (
          <Input
            {...renderProps}
            type="number"
            placeholder={optional?.placeholder}
            disabled={optional?.disabled}
            readOnly={optional?.readOnly}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : Number(e.target.value);
              renderProps.onChange(value);
            }}
          />
        )}
      />
      <FlexErrorMessage errors={optional.errors} name={name} />
    </div>
  );
}

/**
 * Renderiza um input do tipo textarea
 * @function
 * @param {FlexControllerV2} props
 * @returns {React.FC | null}
 */
function FlexControllerV2TextArea({ control, name, type, ...optional }) {
  return (
    <div>
      <FlexLabel
        label={optional?.label}
        required={optional?.required}
        title={optional?.title}
      />
      <Controller
        {...{ control, name, type, ...optional }}
        render={(renderProps) => (
          <Input
            {...renderProps}
            type="textarea"
            placeholder={optional?.placeholder}
            rows={optional?.rows ?? 3}
          />
        )}
      />
      <FlexErrorMessage errors={optional.errors} name={name} />
    </div>
  );
}
